// Social Media threat matrix — which scams hit which platform, and how hard.
//
// Transcribed from the "SM Matrix" source sheet. Two deliberate changes:
//
//  1. Severity labels: the sheet writes "Sever" throughout; rendered as SEVERE.
//     The three levels are Severe > High > Elevated. This is a separate scale
//     from the scanner's risk taxonomy (legit/high/elevated/low) — it grades how
//     dangerous a scam TYPE is on a platform, not how risky a domain is.
//  2. Obvious spelling slips in the source prose are corrected (Comprise ->
//     Compromise, Onine -> Online, Innvation -> Innovation, comprised ->
//     compromised, protray -> portray, Imintation -> Imitation, and similar).
//     No wording, claim or severity was changed.
//
// `severity: null` means the source sheet left that cell blank — it renders as
// "—" (not rated) rather than being guessed at. Inventing a rating on a security
// matrix would be worse than admitting the gap.

export const PLATFORMS = ['Facebook', 'Instagram', 'X — Twitter', 'LinkedIn', 'WhatsApp'];

export const SEVERITY_STYLES = {
  Severe:   { label: 'Severe',   cell: 'bg-[#B02418] text-white' },
  High:     { label: 'High',     cell: 'bg-[#D97706] text-white' },
  Elevated: { label: 'Elevated', cell: 'bg-[#EAB308] text-black' },
};

// Each category holds its threats; `ratings` is index-aligned to PLATFORMS.
const SOCIAL_MEDIA_MATRIX = [
  {
    category: 'Account Compromise / Phishing / Smishing Attacks',
    threats: [
      {
        title: 'Friend Request',
        description:
          'Accepting a stranger’s friend request is a gateway to being scammed. They appear legitimate, but a scammer may use a fake image to enhance their profile picture. They claim to have mutual friends to convince you to connect — don’t.',
        ratings: ['Severe', 'Severe', 'Severe', 'Severe', 'Severe'],
      },
      {
        title: 'Out-of-the-Blue Account Alert',
        description:
          'A notification claims your account was compromised or your profile needs immediate attention, and provides a link to a fake website designed to collect your password and two-factor code. Account security actions should be checked from platform settings or the in-app Support Inbox, not through a random message link.',
        ratings: ['Severe', 'Severe', 'Severe', 'High', 'Severe'],
      },
      {
        title: 'Technical Support',
        description:
          'These messages claim your page, ad account, or personal profile will be suspended unless you appeal immediately. The link often leads to a fake login page that collects your password, two-factor code, business manager access, or payment details. Real account security actions should be checked from platform settings or the in-app Support Inbox, not through a random message link.',
        ratings: ['Severe', 'Severe', 'Severe', 'Severe', 'Severe'],
      },
      {
        title: '“Is This You?” Links',
        description:
          'A friend sends a message — a video, photo tag, prize link, or “is this you?” — because they have been compromised. The scammer is attempting to steal your login or push a malicious browser extension, fake update, or file download.',
        ratings: ['Severe', 'Severe', 'Severe', 'Elevated', 'Severe'],
      },
      {
        title: 'Account Recovery and “Hacker Help”',
        description:
          'You post about an account issue and a scammer sends a DM offering a “recovery expert” who can restore access for a payment, usually by an unusual payment method. Account recovery is free — go through the platform’s own help pages.',
        ratings: ['Severe', 'Severe', 'Severe', 'Severe', 'Severe'],
      },
    ],
  },
  {
    category: 'Confidence Scams',
    threats: [
      {
        title: 'Bot Swarms',
        description:
          'Under any popular post — especially from crypto figures, tech leaders, or financial personalities — bot accounts flood the replies with scam promotions. They use the original poster’s profile picture and a slightly modified name so the replies read as official follow-up comments.',
        // The source sheet left every platform cell blank for this row (and
        // repeated the description three times; de-duplicated here). Rated
        // Severe across the board per Austin, 2026-10-01.
        ratings: ['Severe', 'Severe', 'Severe', 'Severe', 'Severe'],
      },
      {
        title: 'Investment Scams',
        description:
          'Appears as a too-good-to-be-true opportunity promising fast cash or guaranteed returns, much like crypto scams. Scammers portray a flashy lifestyle, urging you to invest and reap huge profits with no downside. The scammer’s real goal is to take your money and vanish, leaving you with no recourse.',
        ratings: ['Severe', 'Severe', 'Severe', 'Severe', 'Severe'],
      },
      {
        title: 'Lottery / Sweepstakes',
        description:
          'Tricks users into believing they have won a large cash prize in a random drawing, then asks for payment processing or delivery fees by an unusual payment method such as gift cards or cryptocurrency. The purpose is to steal your money and gather enough personal data to fuel other identity theft frauds.',
        ratings: ['Severe', 'Severe', 'Severe', 'Severe', 'Severe'],
      },
      {
        title: 'Charity Scams',
        description:
          'Fake or misleading organizations solicit donations but use little or none of the money for the stated cause. Scammers exploit emotional appeals — natural disasters, health crises, animal welfare — to pressure donors into giving quickly. They may use emails, social posts, crowdfunding platforms, cold calls, or AI-generated images and video to appear legitimate.',
        ratings: ['Severe', 'Severe', 'Severe', 'Severe', 'Severe'],
      },
      {
        title: 'Government Impersonation',
        description:
          'A scammer pretends to be an official from agencies like the FTC, IRS, SSA, Medicare, or law enforcement to steal money or personal information, often using threats, fake caller ID, or urgent payment demands.',
        ratings: ['Severe', 'Severe', 'Severe', 'Severe', 'Severe'],
      },
    ],
  },
  {
    category: 'Employment Scams',
    threats: [
      {
        title: 'Job Scams',
        description:
          'Fake job offers start with very limited work requirements, promising easy money for minimal effort. Scammers pose as recruiters and direct you to phony applications, or message you directly asking for personal details like your contact info or SSN.',
        ratings: ['Severe', 'Severe', 'Severe', 'Severe', 'Severe'],
      },
      {
        title: 'Innovation Award',
        description:
          'Recognition awards appear legitimate and a way to stand out from the crowd — except you must pay for a “who cares” badge to validate your career status.',
        ratings: ['High', 'High', 'High', 'Severe', 'High'],
      },
    ],
  },
  {
    category: 'Online Shopping',
    threats: [
      {
        title: '“Wow, That’s a Deal”',
        description:
          'If it looks too good to be true, it is — imitation or fake products. These scams run slick, professional ads linking to shady websites that request personal information and payment details.',
        ratings: ['Severe', 'High', 'High', 'Severe', 'High'],
      },
    ],
  },
  {
    category: 'Digital Intimidation',
    threats: [
      {
        title: 'Extortion',
        description:
          'Direct messages or emails use dark psychology techniques to befriend and manipulate the target, then escalate to threats. Scammers claim your computer was hacked and that they hold suggestive or intimate photos; to keep them private you must pay, or they threaten to share the images with your contacts or post them online.',
        ratings: ['Severe', 'Severe', 'Severe', 'Severe', 'Severe'],
      },
    ],
  },
];

export default SOCIAL_MEDIA_MATRIX;
